import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';

const isActiveUser = (u) => u.isActive ?? true;

const UserOptions = ({ users, exclude, activeOnly = false }) =>
    users
        .filter((u) => u.id !== exclude && (!activeOnly || isActiveUser(u)))
        .map((u) => (
            <SelectItem key={u.id} value={String(u.id)}>
                <span className="font-medium">{u.name}</span>
                <span className="ml-2 text-slate-500">{u.email}</span>
            </SelectItem>
        ));

export default function SubstitutionDialog({ children, substitution, users, existingUserIds = [], onSave }) {

    const isEdit = !!substitution;
    const [open, setOpen] = useState(false);
    const [userId, setUserId] = useState('');
    const [substituteUserId, setSubstituteUserId] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setUserId(substitution?.user?.userId ? String(substitution.user.userId) : '');
            setSubstituteUserId(
                substitution?.substitute?.userId ? String(substitution.substitute.userId) : ''
            );
            setError('');
        }
    }, [open, substitution]);

    // A user can have at most one active substitute; exclude users that already
    // have one (except the user being edited).
    const availablePrimaryUsers = useMemo(() => {
        if (isEdit) return users;
        return users.filter((u) => !existingUserIds.includes(u.id));
    }, [users, existingUserIds, isEdit]);

    const handleSubmit = async () => {
        if (!userId) {
            setError('Select the user to be substituted.');
            return;
        }
        if (!substituteUserId) {
            setError('Select a substitute.');
            return;
        }
        if (userId === substituteUserId) {
            setError('A user cannot be their own substitute.');
            return;
        }
        const substitute = users.find((u) => u.id === Number(substituteUserId));
        if (!substitute || !isActiveUser(substitute)) {
            setError('The substitute must be an active user.');
            return;
        }

        const payload = isEdit
            ? { substituteUserId: Number(substituteUserId) }
            : { userId: Number(userId), substituteUserId: Number(substituteUserId) };

        const ok = await onSave(payload);
        if (ok) {
            setOpen(false);
        } else {
            setError('Something went wrong saving the substitution.');
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{isEdit ? 'Edit Substitution' : 'Add Substitution'}</DialogTitle>
                    <DialogDescription>
                        A stand-in approver for a user who cannot fulfil a workflow step (e.g. the submitter of a
                        payment, or an inactive user). Applies to custom workflows only.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col gap-4 py-2">
                    <div className="flex flex-col gap-2">
                        <Label>User</Label>
                        <Select value={userId} onValueChange={setUserId} disabled={isEdit}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <UserOptions users={availablePrimaryUsers} exclude={Number(substituteUserId)} />
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="flex flex-col gap-2">
                        <Label>Substitute</Label>
                        <Select value={substituteUserId} onValueChange={setSubstituteUserId}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select substitute" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <UserOptions users={users} exclude={Number(userId)} activeOnly />
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>

                    {error && <p className="text-sm text-red-600 mb-0">{error}</p>}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button className="bg-yavrio-green" onClick={handleSubmit}>
                        {isEdit ? 'Save Substitution' : 'Add Substitution'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
